import type { components } from './types.gen'

// PERF-6 — transport core, split out of the 5K-line client.ts so the boot
// graph (main.tsx, auth.store, i18n) pulls only the fetch/auth runtime and
// never the full `api` domain surface. The `api` object lives in client.ts
// and is emitted as its own lazy chunk (see vite manualChunks 'api').

export const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8080'

export type ApiError = {
  code: string
  message: string
  field?: string | null
  status?: number
}

export class HttpError extends Error {
  readonly code: string
  readonly status: number
  readonly field?: string | null

  constructor(error: ApiError) {
    super(error.message)
    this.name = 'HttpError'
    this.code = error.code
    this.status = error.status ?? 0
    this.field = error.field ?? null
  }
}

// PERF audit Lot 3 (2026-07-09): every fetch below used to run with no
// timeout at all. When the backend stalls (pool exhaustion, cold start —
// see docs/audit-perf/rapport-audit.md), the promise never settles, so
// React Query stays stuck in `isLoading` forever and the UI shows an
// infinite loader. Wrapping fetch with AbortController turns that hang
// into a rejection the caller can actually react to.
const REQUEST_TIMEOUT_MS = 15_000
// Certaines opérations sont des traitements par lot côté serveur (calcul de
// paie sur tout l'effectif) qui dépassent largement le timeout par défaut.
// Le serveur leur alloue un contexte détaché de 5 min ; le client s'aligne,
// sinon il avorte à 15 s alors que le traitement aboutit derrière.
export const LONG_RUNNING_TIMEOUT_MS = 300_000
const REFRESH_TIMEOUT_MS = 5_000
const BOOTSTRAP_TIMEOUT_MS = 8_000

async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError'
}

// ── Hooks wired from outside (stores/auth.store.ts) to avoid circular imports.
// SEC-1: the refresh token now lives in an HttpOnly cookie, so the bridge
// no longer exposes getRefreshToken — refresh calls just hit the cookie.
type AuthBridge = {
  getAccessToken: () => string | null
  setTokens: (tokens: components['schemas']['TokenPair']) => void
  clear: () => void
}

let authBridge: AuthBridge | null = null

export function registerAuthBridge(bridge: AuthBridge): void {
  authBridge = bridge
}

let currentLocale: 'fr' | 'en' = 'fr'
export function setApiLocale(locale: 'fr' | 'en'): void {
  currentLocale = locale
}

// ── Shared refresh promise so parallel 401s don't trigger N refresh calls.
let refreshInFlight: Promise<components['schemas']['TokenPair'] | null> | null = null

// SEC-1: refresh is driven entirely by the HttpOnly cookie. The body
// stays empty so the backend reads `amanet_rt` from the request cookie,
// rotates it server-side, and returns a new Set-Cookie + access token.
export async function refreshTokens(): Promise<components['schemas']['TokenPair'] | null> {
  if (!authBridge) return null
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    try {
      const res = await fetchWithTimeout(
        `${BASE_URL}/v1/auth/refresh`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        },
        REFRESH_TIMEOUT_MS,
      )
      if (!res.ok) {
        authBridge?.clear()
        return null
      }
      const body = (await res.json()) as { data: components['schemas']['TokenPair'] }
      authBridge?.setTokens(body.data)
      return body.data
    } catch {
      authBridge?.clear()
      return null
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

// PERF-8 — single-round-trip cold start. Folds the cookie-driven refresh
// and the /me read into one request so the SPA hydrates auth state without
// two serial round-trips on boot. Returns null (and clears the bridge) when
// the cookie is missing/expired, mirroring refreshTokens().
export async function bootstrapAuth(): Promise<components['schemas']['BootstrapResponse'] | null> {
  if (!authBridge) return null
  try {
    const res = await fetchWithTimeout(
      `${BASE_URL}/v1/auth/bootstrap`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      },
      BOOTSTRAP_TIMEOUT_MS,
    )
    if (!res.ok) {
      authBridge?.clear()
      return null
    }
    const body = (await res.json()) as {
      data: components['schemas']['BootstrapResponse']
    }
    authBridge?.setTokens(body.data.tokens)
    return body.data
  } catch {
    authBridge?.clear()
    return null
  }
}

type FetchOptions = RequestInit & { skipAuth?: boolean; timeoutMs?: number }

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { skipAuth, timeoutMs, ...init } = options

  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
  const doRequest = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Accept-Language': currentLocale,
    }
    if (!isFormData) headers['Content-Type'] = 'application/json'
    if (token && !skipAuth) headers.Authorization = `Bearer ${token}`
    for (const [k, v] of Object.entries(init.headers ?? {})) {
      if (typeof v === 'string') headers[k] = v
    }
    // credentials:'include' is mandatory cross-site for the refresh
    // cookie (SEC-1). It's a no-op when the API is same-origin.
    try {
      return await fetchWithTimeout(
        `${BASE_URL}${path}`,
        { ...init, credentials: 'include', headers },
        timeoutMs ?? REQUEST_TIMEOUT_MS,
      )
    } catch (err) {
      if (isAbortError(err)) {
        throw new HttpError({
          code: 'TIMEOUT',
          message: 'Request timed out',
          status: 0,
        })
      }
      throw err
    }
  }

  const token = skipAuth ? null : (authBridge?.getAccessToken() ?? null)
  let res = await doRequest(token)

  if (res.status === 401 && !skipAuth && authBridge) {
    const refreshed = await refreshTokens()
    if (refreshed) {
      res = await doRequest(refreshed.access_token)
    }
  }

  if (!res.ok) {
    let err: ApiError = { code: 'NETWORK_ERROR', message: res.statusText, status: res.status }
    try {
      const body = (await res.json()) as { error?: ApiError }
      if (body.error) err = { ...body.error, status: res.status }
    } catch {
      // non-JSON body — fallback to statusText
    }
    throw new HttpError(err)
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  if (!text) {
    throw new HttpError({
      code: 'EMPTY_RESPONSE',
      message: 'Le serveur a renvoyé une réponse vide',
      status: res.status,
    })
  }
  return JSON.parse(text) as T
}

// apiFetchBlob mirrors apiFetch's auth/retry/timeout envelope but returns
// the raw Response body as a Blob — required for non-JSON downloads such
// as the customs note-detail PDF. The 401→refresh path is reproduced so a
// stale access token doesn't force the caller to reload the page.
export async function apiFetchBlob(
  path: string,
  options: FetchOptions = {},
): Promise<Blob> {
  const { skipAuth, timeoutMs, ...init } = options

  const doRequest = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      Accept: '*/*',
      'Accept-Language': currentLocale,
    }
    if (token && !skipAuth) headers.Authorization = `Bearer ${token}`
    for (const [k, v] of Object.entries(init.headers ?? {})) {
      if (typeof v === 'string') headers[k] = v
    }
    try {
      return await fetchWithTimeout(
        `${BASE_URL}${path}`,
        { ...init, credentials: 'include', headers },
        timeoutMs ?? REQUEST_TIMEOUT_MS,
      )
    } catch (err) {
      if (isAbortError(err)) {
        throw new HttpError({
          code: 'TIMEOUT',
          message: 'Request timed out',
          status: 0,
        })
      }
      throw err
    }
  }

  const token = skipAuth ? null : (authBridge?.getAccessToken() ?? null)
  let res = await doRequest(token)

  if (res.status === 401 && !skipAuth && authBridge) {
    const refreshed = await refreshTokens()
    if (refreshed) {
      res = await doRequest(refreshed.access_token)
    }
  }

  if (!res.ok) {
    let err: ApiError = { code: 'NETWORK_ERROR', message: res.statusText, status: res.status }
    try {
      const body = (await res.json()) as { error?: ApiError }
      if (body.error) err = { ...body.error, status: res.status }
    } catch {
      // non-JSON body — fallback to statusText
    }
    throw new HttpError(err)
  }

  return res.blob()
}
